import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Preferences } from "../../profile/objects/preferences";
import { cloneDeep } from "lodash";
import { setMarginPreference } from "../utils/setMarginPreference";
import { MarginFlightState } from "../objects/marginFlightstate";
import { EMPTY_MARGIN_FLIGHT_LIST_CONFIGURATION, EMPTY_MARGIN_LIST_CRITERIA } from "../constants/marginFlightConstants";
import { MarginFlightListCriteria } from "../objects/marginFlightList";
import { Column } from "../objects/column";
import { ClientBranchCompany } from "../objects/company";
import { Margin } from "../../margin/objects/margin";
import { MarginForm } from "../objects/marginFlightForm";

const initialState: MarginFlightState = {
    margins: [],
    refresh_list: false,
    margins_configuration: EMPTY_MARGIN_FLIGHT_LIST_CONFIGURATION,
    margins_loading: false,
    margins_count: 0,
    margins_criteria: EMPTY_MARGIN_LIST_CRITERIA,
    suspended_margin: null,

    margin: null,
    margin_form: null,

    company: null,

    first_preference_loaded: false
};



export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');


const setPreferenceReducer = (state: MarginFlightState, action: PayloadAction<Preferences[]>): MarginFlightState => {
    const { margins_preferences, margins_columns, margins_order_by, margins_order } = setMarginPreference(action.payload, state.margins_configuration.columns);
    const margin_pref = {
        margins_configuration: { ...state.margins_configuration, preferences: margins_preferences, columns: margins_columns },
        margins_criteria: { ...state.margins_criteria, order: margins_order, order_by: margins_order_by }
    };
    return {
        ...state,
        ...margin_pref,
        first_preference_loaded: true
    };
};



const setSuspendedMarginReducer = (state: MarginFlightState, action: PayloadAction<Margin | null>): MarginFlightState => ({
    ...state, suspended_margin: action.payload
});

const addMarginsConfigurationPreferenceReducer = (state: MarginFlightState, action: PayloadAction<Preferences[]>): MarginFlightState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: [...state.margins_configuration.preferences, ...action.payload] }
});

const setMarginsConfigurationPreferenceReducer = (state: MarginFlightState, action: PayloadAction<Preferences[]>): MarginFlightState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: action.payload }
});

const toggleColumnMarginsReducer = (state: MarginFlightState, action: PayloadAction<number>): MarginFlightState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const setMarginFlightListReducer = (state: MarginFlightState, action: PayloadAction<Margin[]>): MarginFlightState => ({
    ...state, margins: action.payload
});

const refreshListReducer = (state: MarginFlightState): MarginFlightState => ({
    ...state, refresh_list: !state.refresh_list
});

const updateMarginFlightListCriteriaReducer = (state: MarginFlightState, action: PayloadAction<MarginFlightListCriteria>): MarginFlightState => ({
    ...state, margins_criteria: action.payload
});
const updateMarginReducer = (state: MarginFlightState, action: PayloadAction<Margin | null>): MarginFlightState => ({
    ...state, margin: action.payload
});
const updateMarginFormReducer = (state: MarginFlightState, action: PayloadAction<MarginForm | null>): MarginFlightState => ({
    ...state, margin_form: action.payload
});

const updateMarginFlightListLoadingReducer = (state: MarginFlightState, action: PayloadAction<boolean>): MarginFlightState => ({
    ...state, margins_loading: action.payload
});
const updateMarginFlightListCountReducer = (state: MarginFlightState, action: PayloadAction<number>): MarginFlightState => ({
    ...state, margins_count: action.payload
});
const setMarginColumnsReducer = (state: MarginFlightState, action: PayloadAction<Column[]>): MarginFlightState => ({
    ...state, margins_configuration: { ...state.margins_configuration, columns: action.payload },
});

const setCompanyReducer = (state: MarginFlightState, action: PayloadAction<ClientBranchCompany | null>): MarginFlightState => ({
    ...state, company: action.payload,
});

export const marginFlight = createSlice({
    name: 'margin_flight',
    initialState,
    reducers: {
        setSuspendedMargin: setSuspendedMarginReducer,
        addMarginsConfigurationPreference: addMarginsConfigurationPreferenceReducer,
        setMarginsConfigurationPreference: setMarginsConfigurationPreferenceReducer,
        toggleColumnMargins: toggleColumnMarginsReducer,
        setMarginFlightList: setMarginFlightListReducer,
        refreshList: refreshListReducer,
        updateMarginFlightListCriteria: updateMarginFlightListCriteriaReducer,
        updateMargin: updateMarginReducer,
        updateMarginForm: updateMarginFormReducer,
        updateMarginFlightListLoading: updateMarginFlightListLoadingReducer,
        updateMarginFlightListCount: updateMarginFlightListCountReducer,
        setMarginColumns: setMarginColumnsReducer,

        setCompany: setCompanyReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addMarginsConfigurationPreference,
    setMarginsConfigurationPreference,
    setMarginColumns,
    toggleColumnMargins,
    setMarginFlightList,
    refreshList,
    updateMarginFlightListCriteria,
    updateMarginFlightListLoading,
    updateMarginFlightListCount,
    updateMargin,
    updateMarginForm,
    setSuspendedMargin,
    setCompany
} = marginFlight.actions;